/* eslint-disable */
/*
 * h264-params-synth.js — synthesise an H.264 parameter set for a stream that carries none.
 *
 * WHY THIS EXISTS
 * ---------------
 * A recording killed mid-write loses its `moov`, and with it the `avcC` box holding
 * SPS/PPS. The slices survive in `mdat` and are perfectly decodable — but only if a
 * parameter set describes them. Untrunc-class tools solve this by asking the user for a
 * DONOR: a healthy clip from the same camera. Measured on our own traffic that request
 * is itself the failure — of 1222 sessions shown the donor panel only 252 supplied one,
 * and the standalone (donor-less) path then bailed `no_in_stream_params` in 317 sessions
 * over 30 days. `/photo-repair` hit the identical wall with JPEG headers and answered it
 * the same way: rebuild the header instead of demanding a second file.
 *
 * WHAT MAKES IT POSSIBLE (all figures measured, see tests/Unit/H264ParamsSynthTest.php
 * and the fixture harness that produced them)
 * ------------------------------------------
 * A decoder does not need the ORIGINAL parameter set, only one that agrees with the
 * slices. Per-field perturbation of a real SPS showed the fields fall in two classes:
 *
 *   MUST match (wrong value => hundreds of decode errors, PSNR ~11 dB = garbage):
 *     log2_max_frame_num_minus4, pic_order_cnt_type, log2_max_poc_lsb_minus4,
 *     resolution, entropy_coding_mode, and the PPS flags that shift slice-header bits.
 *   FREE (wrong value => 0 decode errors, PSNR inf = bit-identical output):
 *     level_idc (30/40/51 all fine), gaps_in_frame_num_allowed, and effectively
 *     direct_8x8_inference (2 errors, PSNR 66.8 dB — visually identical).
 *
 * Substituting a foreign-but-compatible parameter set into a real file reconstructed it
 * bit-exactly (PSNR inf, 180/180 frames), while an incompatible one fell apart at once.
 * So the search is small and its verdict is unambiguous.
 *
 * HOW THE SEARCH IS SCORED
 * ------------------------
 * The must-match timing fields are recovered WITHOUT a decoder, by parsing slice headers
 * under each candidate and scoring how sane the result is. Wrong bit widths misalign
 * every later field, and the damage is loud: quantiser parameters leave 0..51, slice
 * types leave 0..9, IDR ids explode. This is the same shape of self-check that
 * `/photo-repair` uses (MCU density per KB of scan) — and it carries the same warning:
 * a threshold is a property of the probe that produced it, so `scoreCandidate` returns
 * raw counts and the caller decides, rather than baking in a magic number here.
 *
 * Resolution cannot be read from slice headers at all — it lives only in the SPS — so it
 * is drawn from a list of camera resolutions and settled by an actual decode. The caller
 * supplies that decoder (`opts.validate`): WebCodecs in the browser, ffmpeg in tests.
 * Nothing is ever returned unvalidated, which is what makes this strictly additive: it
 * can only turn "nothing" into "something that was proven to decode".
 *
 * KNOWN COVERAGE GAP — measured, not suspected
 * --------------------------------------------
 * Feature-rich encodes are NOT yet covered. A `-preset medium -crf 20` file (H.264 High,
 * the shape a transcoder or a better camera writes) needs four more fields that this
 * search fixes rather than varies:
 *
 *     weighted_pred_flag = 1 · weighted_bipred_idc = 2 ·
 *     num_ref_idx_l0_default_active_minus1 = 2 · transform_8x8_mode_flag = 1
 *
 * Against such a file the synthesis returns NOTHING — verified, and verified further that
 * the file's OWN parameter set is accepted by this very validator (8/8 frames). So the
 * mechanism is sound and the gap is search coverage, nothing deeper.
 *
 * Two of those fields (weighted_pred, num_ref_idx) shift slice-header bits, so stage 1 can
 * find them for free; transform_8x8_mode does not and would cost a x2 on the decoder pass.
 * Whoever widens this: the measurement that matters is `untrunc_params_synthesised` per
 * download, not the size of the grid.
 *
 * ⚠️ How the gap stayed hidden: every fixture recovered cleanly because every fixture was
 * encoded `-preset ultrafast`, which happens to write the exact defaults this code
 * assumes. A fixture set homogeneous in the field under test proves nothing about it.
 */
(function (global) {
    'use strict';

    /* ------------------------------------------------------------------ bit plumbing */

    function BitReader(bytes) { this.b = bytes; this.p = 0; }
    BitReader.prototype.left = function () { return this.b.length * 8 - this.p; };
    BitReader.prototype.bit = function () {
        if (this.p >= this.b.length * 8) throw new Error('eof');
        var v = (this.b[this.p >> 3] >> (7 - (this.p & 7))) & 1; this.p++; return v;
    };
    BitReader.prototype.bits = function (n) { var v = 0; for (var i = 0; i < n; i++) v = ((v << 1) | this.bit()) >>> 0; return v; };
    BitReader.prototype.ue = function () {
        var z = 0;
        while (this.bit() === 0) { if (++z > 31) throw new Error('ue overflow'); }
        return z === 0 ? 0 : (((1 << z) >>> 0) - 1 + this.bits(z));
    };
    BitReader.prototype.se = function () { var k = this.ue(); return (k & 1) ? ((k + 1) >> 1) : -(k >> 1); };

    function BitWriter() { this.out = []; this.cur = 0; this.n = 0; }
    BitWriter.prototype.bit = function (v) {
        this.cur = ((this.cur << 1) | (v & 1)) & 0xff;
        if (++this.n === 8) { this.out.push(this.cur); this.cur = 0; this.n = 0; }
    };
    BitWriter.prototype.bits = function (v, n) { for (var i = n - 1; i >= 0; i--) this.bit((v >> i) & 1); };
    BitWriter.prototype.ue = function (v) {
        v = v + 1; var n = 0, t = v;
        while (t > 1) { t >>= 1; n++; }
        for (var i = 0; i < n; i++) this.bit(0);
        for (var j = n; j >= 0; j--) this.bit((v >> j) & 1);
    };
    BitWriter.prototype.se = function (v) { this.ue(v <= 0 ? -2 * v : 2 * v - 1); };
    BitWriter.prototype.trailing = function () { this.bit(1); while (this.n !== 0) this.bit(0); };
    BitWriter.prototype.bytes = function () { return new Uint8Array(this.out); };

    /* Emulation-prevention: 0x000003 in the byte stream stands for 0x0000. Parsing a raw
       NAL without stripping it silently shifts every field after the first occurrence. */
    function unescapeRbsp(b) {
        var o = [], zeros = 0;
        for (var i = 0; i < b.length; i++) {
            if (zeros === 2 && b[i] === 3) { zeros = 0; continue; }
            o.push(b[i]);
            zeros = b[i] === 0 ? zeros + 1 : 0;
        }
        return new Uint8Array(o);
    }
    function escapeRbsp(b) {
        var o = [], zeros = 0;
        for (var i = 0; i < b.length; i++) {
            if (zeros === 2 && b[i] <= 3) { o.push(3); zeros = 0; }
            o.push(b[i]);
            zeros = b[i] === 0 ? zeros + 1 : 0;
        }
        return new Uint8Array(o);
    }

    /* ------------------------------------------------------------------ SPS / PPS build */

    var HIGH_PROFILES = [100, 110, 122, 244, 44, 83, 86, 118, 128, 138, 139, 134, 135];

    /*
     * Build an SPS. `level_idc` is pinned to 5.1 rather than guessed: the perturbation
     * measurement showed level is FREE (30/40/51 all decoded bit-identically), and 5.1
     * is high enough that no phone resolution exceeds it. Guessing low would be the one
     * way to turn a free field into a fatal one.
     */
    function buildSps(f) {
        var w = new BitWriter();
        var profile = f.profile_idc || 100;
        w.bits(profile, 8);
        w.bits(f.constraint_flags || 0, 8);
        w.bits(f.level_idc || 51, 8);
        w.ue(0); // seq_parameter_set_id
        if (HIGH_PROFILES.indexOf(profile) >= 0) {
            w.ue(1); // chroma_format_idc — 4:2:0; every consumer camera codec we handle
            w.ue(0); // bit_depth_luma_minus8
            w.ue(0); // bit_depth_chroma_minus8
            w.bit(0); // qpprime_y_zero_transform_bypass
            w.bit(0); // seq_scaling_matrix_present
        }
        w.ue(f.log2_max_frame_num_minus4);
        w.ue(f.pic_order_cnt_type);
        if (f.pic_order_cnt_type === 0) w.ue(f.log2_max_poc_lsb_minus4);
        else if (f.pic_order_cnt_type === 1) {
            w.bit(1); // delta_pic_order_always_zero_flag
            w.se(0); w.se(0); w.ue(0);
        }
        w.ue(f.max_num_ref_frames == null ? 4 : f.max_num_ref_frames);
        w.bit(1); // gaps_in_frame_num_value_allowed — FREE per measurement; 1 tolerates
                  // the frame_num jumps a truncated recording inevitably contains.
        w.ue(f.pic_width_in_mbs - 1);
        w.ue(f.pic_height_in_map_units - 1);
        w.bit(1); // frame_mbs_only_flag — progressive; phones do not record fields
        w.bit(1); // direct_8x8_inference_flag
        if (f.crop) {
            w.bit(1);
            w.ue(f.crop.left || 0); w.ue(f.crop.right || 0);
            w.ue(f.crop.top || 0); w.ue(f.crop.bottom || 0);
        } else w.bit(0);
        w.bit(0); // vui_parameters_present — no VUI; fps is carried separately by the
                  // caller's sample timing, so an absent VUI costs nothing.
        w.trailing();
        var body = escapeRbsp(w.bytes());
        var out = new Uint8Array(body.length + 1);
        out[0] = 0x67; // nal_ref_idc=3, type=7 (SPS)
        out.set(body, 1);
        return out;
    }

    function buildPps(f) {
        var w = new BitWriter();
        // The id must be the one the SLICES reference, not a convenient 0: every consumer
        // of this pair filters samples by pps_id, so a mismatch silently discards the whole
        // stream and reads as "nothing recoverable" rather than as a wrong guess.
        w.ue(f.pps_id || 0);
        w.ue(0); // seq_parameter_set_id
        w.bit(f.entropy_coding_mode);
        w.bit(f.bottom_field_pic_order_present || 0);
        w.ue(0); // num_slice_groups_minus1
        w.ue(f.num_ref_idx_l0_minus1 == null ? 0 : f.num_ref_idx_l0_minus1);
        w.ue(f.num_ref_idx_l1_minus1 == null ? 0 : f.num_ref_idx_l1_minus1);
        w.bit(0); // weighted_pred_flag
        w.bits(0, 2); // weighted_bipred_idc
        w.se(f.pic_init_qp_minus26 || 0);
        w.se(0); // pic_init_qs_minus26
        w.se(0); // chroma_qp_index_offset
        w.bit(f.deblocking_filter_control_present == null ? 1 : f.deblocking_filter_control_present);
        w.bit(0); // constrained_intra_pred_flag
        w.bit(0); // redundant_pic_cnt_present_flag
        w.trailing();
        var body = escapeRbsp(w.bytes());
        var out = new Uint8Array(body.length + 1);
        out[0] = 0x68; // nal_ref_idc=3, type=8 (PPS)
        out.set(body, 1);
        return out;
    }

    /* ------------------------------------------------------------------ slice scoring */

    var SLICE_I = [2, 4, 7, 9];

    /*
     * Parse one slice header under a candidate and report whether every field landed in
     * its legal range. Returns null when the header is unparseable under this candidate —
     * which is itself the signal we are looking for.
     */
    /*
     * `nalBody` may be raw (emulation-prevention bytes still in) or an already-unescaped
     * prefix. Callers in a search loop MUST pass the prefix: unescaping inside this
     * function costs a full copy of a multi-kilobyte slice, and the loop calls it once per
     * slice per candidate — measured at 5.0 s for 702 candidates on a real page, which was
     * the single largest cost in the whole synthesis, dwarfing the decoder (18 ms to reject
     * a candidate). See sliceProbes().
     */
    function probeSliceHeader(nalBody, nalType, cand, totalMbs, preUnescaped) {
        try {
            var r = new BitReader(preUnescaped ? nalBody : unescapeRbsp(nalBody));
            var firstMb = r.ue();
            if (firstMb >= totalMbs) return null;
            var sliceType = r.ue();
            if (sliceType > 9) return null;
            var ppsId = r.ue();
            if (ppsId > 255) return null;
            r.bits(cand.log2_max_frame_num_minus4 + 4); // frame_num
            var isIdr = (nalType === 5);
            if (isIdr) { var idrId = r.ue(); if (idrId > 65535) return null; }
            if (cand.pic_order_cnt_type === 0) {
                r.bits(cand.log2_max_poc_lsb_minus4 + 4); // pic_order_cnt_lsb
                if (cand.bottom_field_pic_order_present) r.se();
            }
            var st = sliceType % 5;
            if (st === 1) return null; // B slice — needs list state we do not model here
            if (st === 0) {
                // P slice: num_ref_idx_active_override + ref list modification
                if (r.bit()) r.ue();
                if (r.bit()) { // ref_pic_list_modification_flag_l0
                    var guard = 0;
                    for (;;) {
                        var idc = r.ue();
                        if (idc === 3) break;
                        if (idc > 3 || ++guard > 32) return null;
                        r.ue();
                    }
                }
            }
            // dec_ref_pic_marking (nal_ref_idc is non-zero for the slices we score)
            if (isIdr) { r.bit(); r.bit(); }
            else if (r.bit()) { // adaptive_ref_pic_marking_mode_flag
                var g2 = 0;
                for (;;) {
                    var op = r.ue();
                    if (op === 0) break;
                    if (op > 6 || ++g2 > 32) return null;
                    if (op === 1 || op === 3) r.ue();
                    if (op === 2) r.ue();
                    if (op === 3 || op === 6) r.ue();
                    if (op === 5) break;
                }
            }
            if (cand.entropy_coding_mode && st !== 2) { var ci = r.ue(); if (ci > 2) return null; }
            var qpDelta = r.se();
            var qp = 26 + (cand.pic_init_qp_minus26 || 0) + qpDelta;
            if (qp < 0 || qp > 51) return null;
            return { sliceType: sliceType, qp: qp, ppsId: ppsId, isIdr: isIdr };
        } catch (e) {
            return null;
        }
    }

    /*
     * Score a candidate over a sample of slices. Returns raw counts — the caller sets the
     * bar. A candidate that misaligns the bit stream typically parses a handful of slices
     * by luck and fails the rest, so the ratio separates far more sharply than any single
     * slice can.
     */
    /*
     * Unescape each slice ONCE, keeping only the head — every field this search reads sits
     * within the first bytes of the header, so copying the whole slice per candidate was
     * paying kilobytes for bits.
     */
    var PROBE_PREFIX_BYTES = 96;
    function sliceProbes(slices) {
        var out = [];
        for (var i = 0; i < slices.length; i++) {
            var b = slices[i].body;
            out.push({
                rbsp: unescapeRbsp(b.length > PROBE_PREFIX_BYTES ? b.subarray(0, PROBE_PREFIX_BYTES) : b),
                type: slices[i].type,
            });
        }
        return out;
    }

    function scoreCandidate(slices, cand, totalMbs) {
        var ok = 0, qpSum = 0, ppsIds = {}, topId = 0, topN = 0;
        for (var i = 0; i < slices.length; i++) {
            var pre = slices[i].rbsp !== undefined;
            var res = probeSliceHeader(pre ? slices[i].rbsp : slices[i].body,
                slices[i].type, cand, totalMbs, pre);
            if (!res) continue;
            ok++; qpSum += res.qp;
            ppsIds[res.ppsId] = (ppsIds[res.ppsId] || 0) + 1;
            if (ppsIds[res.ppsId] > topN) { topN = ppsIds[res.ppsId]; topId = res.ppsId; }
        }
        return {
            parsed: ok,
            total: slices.length,
            ratio: slices.length ? ok / slices.length : 0,
            avgQp: ok ? qpSum / ok : 0,
            // Real streams reference a single PPS. Several distinct ids across slices means
            // the reader is walking misaligned bits and inventing them.
            ppsSpread: Object.keys(ppsIds).length,
            ppsId: topId,
        };
    }

    /*
     * pic_init_qp_minus26 candidates, most likely first.
     *
     * This field does NOT shift any bits, so stage 1 cannot see it: a wrong value parses
     * every slice header perfectly and only corrupts the quantiser. Under CABAC that is
     * loud — the arithmetic contexts are initialised from SliceQP, so a wrong value fails
     * to decode at all — which is why the decoder can settle it and why it must be searched
     * rather than assumed.
     *
     * It was assumed 0 at first, and every fixture agreed — because they were all encoded
     * with `-preset ultrafast`, which happens to write 0. A `-preset medium -crf 20` file
     * writes -6, and against it the whole synthesis returned NOTHING (measured). The
     * homogeneous fixture set had hidden the one field it hardcoded.
     *
     * Order: 0 (bitrate-mode default, pic_init_qp 26) first, then the band that CRF-mode
     * encoders write — pic_init_qp tracks the CRF/QP target, so 18..32 covers ordinary
     * camera and transcoder output.
     */
    var INIT_QPS = [0, -6, -4, -8, -2, -5, -3, -7, -1, 1, 2, -9, 3, -10, 4, 5, 6];

    /* Camera resolutions, widest-used first. Portrait variants are separate entries: a
       phone held upright writes 1080x1920 into the SPS and rotates on playback. */
    var RESOLUTIONS = [
        [1920, 1080], [1280, 720], [3840, 2160], [1080, 1920], [720, 1280],
        [2160, 3840], [640, 480], [854, 480], [640, 360], [1920, 1440],
        [1440, 1080], [2560, 1440], [352, 288], [176, 144], [4096, 2160], [1440, 2560],
    ];

    function mbDims(w, h) {
        var mbW = Math.ceil(w / 16), mbH = Math.ceil(h / 16);
        var crop = null;
        var padR = mbW * 16 - w, padB = mbH * 16 - h;
        // Cropping is expressed in chroma units for 4:2:0 — 2 luma samples per unit.
        if (padR || padB) crop = { left: 0, right: padR / 2, top: 0, bottom: padB / 2 };
        return { mbW: mbW, mbH: mbH, crop: crop };
    }

    /*
     * Recover a parameter set for `slices`.
     *
     * opts.validate(sps, pps, meta) → truthy when a real decoder accepted the pair. It is
     * REQUIRED: without it this function would be guessing, and a plausible-looking wrong
     * answer is worse for the user than an honest failure.
     * opts.resolutions — override the candidate list (tests pin it to keep runs short).
     * opts.onProgress(done, total).
     */
    async function recover(slices, opts) {
        opts = opts || {};
        if (typeof opts.validate !== 'function') throw new Error('opts.validate is required');
        var resolutions = opts.resolutions || RESOLUTIONS;

        // Stage 1 — timing fields, scored on slice headers alone (no decoder involved).
        // Bounded by the standard: log2_max_frame_num_minus4 and log2_max_poc_lsb_minus4
        // are both 0..12, and pic_order_cnt_type 0 or 2 covers every camera encoder we
        // have seen (type 1 is vanishingly rare and is skipped rather than guessed at).
        var sample = sliceProbes(slices.slice(0, Math.min(slices.length, 60)));
        // A generous macroblock bound for stage 1: resolution is unknown here, and
        // first_mb_in_slice only needs to be sane, not exact.
        var LOOSE_MBS = 4096 * 2176 / 256;
        var cands = [];
        for (var lf = 0; lf <= 12; lf++) {
            for (var pt = 0; pt <= 2; pt += 2) {
                var lsbRange = pt === 0 ? 12 : 0;
                for (var ll = 0; ll <= lsbRange; ll++) {
                    for (var ecm = 0; ecm <= 1; ecm++) {
                        for (var bfp = 0; bfp <= (pt === 0 ? 1 : 0); bfp++) {
                            cands.push({
                                log2_max_frame_num_minus4: lf,
                                pic_order_cnt_type: pt,
                                log2_max_poc_lsb_minus4: ll,
                                entropy_coding_mode: ecm,
                                bottom_field_pic_order_present: bfp,
                                pic_init_qp_minus26: 0,
                            });
                        }
                    }
                }
            }
        }
        var scored = [];
        for (var ci = 0; ci < cands.length; ci++) {
            var s = scoreCandidate(sample, cands[ci], LOOSE_MBS);
            if (s.parsed === 0) continue;
            scored.push({ cand: cands[ci], score: s });
        }
        scored.sort(function (a, b) {
            // Prefer candidates that parse more slices, then those referencing one PPS.
            if (b.score.ratio !== a.score.ratio) return b.score.ratio - a.score.ratio;
            return a.score.ppsSpread - b.score.ppsSpread;
        });
        if (!scored.length) return null;

        // Stage 2 — resolution, settled by a real decode. Only the timing candidates that
        // survived stage 1 are carried forward, so this stays a short list, not a product.
        var timingShortlist = scored.slice(0, opts.timingDepth || 6);
        var initQps = opts.initQps || INIT_QPS;
        var total = timingShortlist.length * resolutions.length * initQps.length;
        var done = 0;
        // A wall-clock budget, not a candidate cap. Exhausting the full grid measured 18 s
        // on desktop Chromium and would be far longer on the 4 GB phones this cohort
        // actually arrives from — time spent only to end up saying "no". Because both
        // lists are ordered by likelihood (timing by header score, resolutions by how
        // common they are), stopping early discards the least probable candidates: the
        // fixtures that DO recover settled at candidate 2, 4, 7 and 33. A wall-clock
        // bound also self-adjusts to slow hardware, where a fixed count would not.
        var budgetMs = opts.budgetMs == null ? 10000 : opts.budgetMs;
        var startedAt = (global.performance && global.performance.now)
            ? global.performance.now() : Date.now();
        function elapsed() {
            var now = (global.performance && global.performance.now)
                ? global.performance.now() : Date.now();
            return now - startedAt;
        }
        var stoppedEarly = false;
        // Loop order matters more than the loop contents. pic_init_qp sits OUTERMOST with
        // its most common value first, so the entire timing x resolution grid is covered at
        // qp=0 before any other qp is touched. Nested the other way it would have burned the
        // budget on 17 quantiser guesses for the first resolution and reached fewer
        // resolutions than before this dimension existed — a coverage regression dressed as
        // an improvement.
        for (var qi = 0; qi < initQps.length && !stoppedEarly; qi++) {
        for (var ti = 0; ti < timingShortlist.length && !stoppedEarly; ti++) {
            var c = timingShortlist[ti].cand;
            for (var ri = 0; ri < resolutions.length && !stoppedEarly; ri++) {
                var wpx = resolutions[ri][0], hpx = resolutions[ri][1];
                var dims = mbDims(wpx, hpx);
                var sps = buildSps({
                    profile_idc: c.entropy_coding_mode ? 100 : 66,
                    log2_max_frame_num_minus4: c.log2_max_frame_num_minus4,
                    pic_order_cnt_type: c.pic_order_cnt_type,
                    log2_max_poc_lsb_minus4: c.log2_max_poc_lsb_minus4,
                    pic_width_in_mbs: dims.mbW,
                    pic_height_in_map_units: dims.mbH,
                    crop: dims.crop,
                });
                {
                    if (budgetMs > 0 && elapsed() > budgetMs) { stoppedEarly = true; break; }
                    var pps = buildPps({
                        entropy_coding_mode: c.entropy_coding_mode,
                        bottom_field_pic_order_present: c.bottom_field_pic_order_present,
                        pic_init_qp_minus26: initQps[qi],
                        pps_id: timingShortlist[ti].score.ppsId || 0,
                    });
                    done++;
                    if (opts.onProgress) { try { opts.onProgress(done, total); } catch (_) {} }
                    var verdict = await opts.validate(sps, pps, {
                        width: wpx, height: hpx, cand: c, initQp: initQps[qi],
                        score: timingShortlist[ti].score,
                    });
                    if (verdict) {
                        return {
                            sps: sps, pps: pps, width: wpx, height: hpx,
                            initQp: initQps[qi],
                            cand: c, score: timingShortlist[ti].score, verdict: verdict,
                            candidatesTried: done, ms: Math.round(elapsed()),
                        };
                    }
                }
            }
        }
        }
        // Surfaced so the caller can tell "searched everything, this stream is not ours"
        // from "ran out of time" — the two want different answers if the numbers ever say so.
        recover.lastRun = { candidatesTried: done, total: total, stoppedEarly: stoppedEarly, ms: Math.round(elapsed()) };
        return null;
    }

    /*
     * The browser's own decoder as the validator. WebCodecs is the right judge here for
     * the same reason it is the wrong judge elsewhere in this tool: we are not asking
     * "is the user's file good" (a question the browser cannot answer for a codec it
     * lacks) but "does THIS parameter set describe THESE slices" — and a decoder that
     * cannot even be configured for H.264 simply reports so, rather than answering wrong.
     *
     * `slices` are AVCC length-prefixed NALs including their header byte. They are fed as
     * one key chunk with the parameter sets in `description`, which is exactly how an MP4
     * track hands them over — so a pass here means a pass in the rebuilt file.
     */
    function makeWebCodecsValidator(slices, opts) {
        opts = opts || {};
        var maxChunks = opts.maxChunks || 8;
        if (typeof VideoDecoder === 'undefined') return null;
        return async function (sps, pps, meta) {
            var desc = buildAvcCDescription(sps, pps);
            var config = {
                codec: 'avc1.' + toHex2(sps[1]) + toHex2(sps[2]) + toHex2(sps[3]),
                description: desc,
                codedWidth: meta.width,
                codedHeight: meta.height,
                optimizeForLatency: true,
            };
            try {
                var sup = await VideoDecoder.isConfigSupported(config);
                if (!sup || !sup.supported) return null;
            } catch (_) { return null; }

            return await new Promise(function (resolve) {
                var frames = 0, settled = false;
                var timer = setTimeout(function () { finish(); }, opts.timeoutMs || 2500);
                function finish() {
                    if (settled) return;
                    settled = true;
                    clearTimeout(timer);
                    try { dec.close(); } catch (_) {}
                    // Require more than a single frame: a wrong parameter set occasionally
                    // yields one accidental frame before falling apart, and accepting that
                    // would hand the user a one-frame "repair".
                    resolve(frames >= Math.min(3, maxChunks) ? { frames: frames } : null);
                }
                var dec;
                try {
                    dec = new VideoDecoder({
                        output: function (f) { frames++; try { f.close(); } catch (_) {} },
                        error: function () { finish(); },
                    });
                    dec.configure(config);
                } catch (_) { return resolve(null); }
                try {
                    for (var i = 0; i < Math.min(slices.length, maxChunks); i++) {
                        dec.decode(new EncodedVideoChunk({
                            type: i === 0 ? 'key' : 'delta',
                            timestamp: i * 33333,
                            data: slices[i],
                        }));
                    }
                    dec.flush().then(finish, finish);
                } catch (_) { finish(); }
            });
        };
    }

    function toHex2(n) { var s = (n & 0xff).toString(16); return s.length < 2 ? '0' + s : s; }

    /* avcC payload for VideoDecoder.description — same layout an MP4 stsd carries. */
    function buildAvcCDescription(sps, pps) {
        var out = [1, sps[1], sps[2], sps[3], 0xff, 0xe1,
            (sps.length >> 8) & 0xff, sps.length & 0xff];
        for (var i = 0; i < sps.length; i++) out.push(sps[i]);
        out.push(1, (pps.length >> 8) & 0xff, pps.length & 0xff);
        for (var j = 0; j < pps.length; j++) out.push(pps[j]);
        return new Uint8Array(out);
    }

    global.H264ParamsSynth = {
        recover: recover,
        makeWebCodecsValidator: makeWebCodecsValidator,
        buildAvcCDescription: buildAvcCDescription,
        buildSps: buildSps,
        buildPps: buildPps,
        scoreCandidate: scoreCandidate,
        sliceProbes: sliceProbes,
        probeSliceHeader: probeSliceHeader,
        RESOLUTIONS: RESOLUTIONS,
        _bits: { BitReader: BitReader, BitWriter: BitWriter, unescapeRbsp: unescapeRbsp, escapeRbsp: escapeRbsp },
    };
})(typeof window !== 'undefined' ? window : this);
